import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API, getAuthHeaders } from './shared';

export default function OrganizationsTab({ organizations, onCreateOrg, onEditOrg, onDeleteOrg, onRefresh }) {
  const [selectedOrgs, setSelectedOrgs] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkForm, setBulkForm] = useState({ days: 30, tier: 'start' });

  const toggleOrgSelect = (id) => {
    setSelectedOrgs(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllOrgs = () => {
    const selectable = organizations.filter(o => !o.is_system_org);
    if (selectedOrgs.size === selectable.length) {
      setSelectedOrgs(new Set());
    } else {
      setSelectedOrgs(new Set(selectable.map(o => o.id)));
    }
  };

  const executeBulkAction = async (action, extraData = {}) => {
    const ids = [...selectedOrgs];
    const headers = getAuthHeaders();
    try {
      let endpoint, body;
      if (action === 'delete') {
        if (!window.confirm(`Er du sikker på at du vil slette ${ids.length} organisasjoner? Alt data slettes permanent.`)) return;
        endpoint = `${API}/ops/system/bulk/delete-organizations`;
        body = { org_ids: ids };
      } else {
        endpoint = `${API}/ops/system/bulk/update-subscriptions`;
        body = { org_ids: ids, action, ...extraData };
      }
      const res = await axios.post(endpoint, body, { headers });
      toast.success(res.data.message);
      setSelectedOrgs(new Set());
      setBulkAction(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk-operasjon feilet');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Alle organisasjoner ({organizations.length})</h2>
        <button onClick={onCreateOrg} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
          + Opprett organisasjon
        </button>
      </div>

      {/* Bulk action bar */}
      {selectedOrgs.size > 0 && (
        <div className="bg-cyan-900/30 border border-cyan-700/40 rounded-xl p-3 flex items-center gap-3 flex-wrap" data-testid="org-bulk-bar">
          <span className="text-sm text-cyan-300 font-medium">{selectedOrgs.size} valgt</span>
          <div className="h-5 w-px bg-cyan-700/50" />
          <button onClick={() => executeBulkAction('delete')} className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700" data-testid="org-bulk-delete">Slett</button>
          <button onClick={() => setBulkAction('org-extend')} className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700" data-testid="org-bulk-extend">Forleng</button>
          <button onClick={() => setBulkAction('org-trial')} className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-700" data-testid="org-bulk-trial">Opprett prøveperiode</button>
          <button onClick={() => executeBulkAction('suspend')} className="px-3 py-1.5 bg-amber-600 text-white text-xs rounded-lg hover:bg-amber-700" data-testid="org-bulk-suspend">Suspender</button>
          <button onClick={() => executeBulkAction('activate')} className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700" data-testid="org-bulk-activate">Aktiver</button>
          <button onClick={() => setSelectedOrgs(new Set())} className="px-3 py-1.5 text-gray-400 text-xs hover:text-white">Avbryt</button>
        </div>
      )}

      {bulkAction === 'org-extend' && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex items-center gap-4" data-testid="org-bulk-extend-form">
          <span className="text-sm text-gray-300">Forleng {selectedOrgs.size} organisasjoner med</span>
          <input type="number" value={bulkForm.days} onChange={e => setBulkForm({...bulkForm, days: parseInt(e.target.value) || 0})} className="w-20 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm" data-testid="org-bulk-extend-days" />
          <span className="text-sm text-gray-400">dager</span>
          <button onClick={() => executeBulkAction('extend', { days: bulkForm.days })} className="px-3 py-1.5 bg-cyan-600 text-white text-xs rounded-lg" data-testid="org-bulk-extend-submit">Utfør</button>
          <button onClick={() => setBulkAction(null)} className="text-gray-500 text-xs hover:text-white">Avbryt</button>
        </div>
      )}

      {bulkAction === 'org-trial' && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex items-center gap-4 flex-wrap" data-testid="org-bulk-trial-form">
          <span className="text-sm text-gray-300">Opprett prøveperiode for {selectedOrgs.size} organisasjoner:</span>
          <select value={bulkForm.tier} onChange={e => setBulkForm({...bulkForm, tier: e.target.value})} className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm">
            <option value="start">Start</option>
            <option value="pro">Pro</option>
          </select>
          <input type="number" value={bulkForm.days} onChange={e => setBulkForm({...bulkForm, days: parseInt(e.target.value) || 0})} className="w-20 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm" />
          <span className="text-sm text-gray-400">dager</span>
          <button onClick={() => executeBulkAction('create_trial', { days: bulkForm.days, tier: bulkForm.tier })} className="px-3 py-1.5 bg-cyan-600 text-white text-xs rounded-lg" data-testid="org-bulk-trial-submit">Utfør</button>
          <button onClick={() => setBulkAction(null)} className="text-gray-500 text-xs hover:text-white">Avbryt</button>
        </div>
      )}

      <div className="bg-gray-800 rounded-lg divide-y divide-gray-700">
        <div className="p-3 flex items-center gap-3 bg-gray-800/80">
          <input type="checkbox" checked={selectedOrgs.size === organizations.filter(o => !o.is_system_org).length && selectedOrgs.size > 0} onChange={toggleAllOrgs} className="w-4 h-4 accent-cyan-500" data-testid="org-select-all" />
          <span className="text-xs text-gray-500">Velg alle</span>
        </div>
        {organizations.map(org => (
          <div key={org.id} className={`p-4 flex items-center gap-3 ${selectedOrgs.has(org.id) ? 'bg-cyan-900/10' : ''}`}>
            {!org.is_system_org ? (
              <input type="checkbox" checked={selectedOrgs.has(org.id)} onChange={() => toggleOrgSelect(org.id)} className="w-4 h-4 accent-cyan-500 flex-shrink-0" data-testid={`org-select-${org.id}`} />
            ) : <div className="w-4" />}
            <div className="flex-1">
              <div className="font-medium flex items-center gap-2">
                {org.name}
                {org.is_test_org && <span className="px-2 py-0.5 bg-yellow-600 text-xs rounded">TEST</span>}
                {org.is_system_org && <span className="px-2 py-0.5 bg-red-600 text-xs rounded">SYSTEM</span>}
              </div>
              <div className="text-sm text-gray-400">Org.nr: {org.orgnr} &bull; {org.user_count} brukere</div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => onEditOrg(org)} className="text-blue-400 hover:text-blue-300 text-sm">Rediger</button>
              {!org.is_system_org && (
                <button onClick={() => onDeleteOrg(org)} className="text-red-400 hover:text-red-300 text-sm">Slett</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
